/**
 * The one skeleton drawing for a canvas: the upload page's on-device analysis (frame by frame as it
 * runs, then in review) and the session page's processing clip both draw through this, so a body
 * looks the same wherever the on-device pose is shown.
 *
 * The bones are the live view's own list (liveAnalysis.SKELETON_BONES, by landmark name), so there
 * is one definition of which joints are joined.
 */
import { SKELETON_BONES } from "../live/liveAnalysis";

export type SkeletonPoint = { x: number; y: number };

export type SkeletonStyle = {
  bone?: string;
  joint?: string;
  /** Joints to dot. Default: every joint that ends a bone. */
  joints?: readonly string[];
  /** How wide the canvas is SHOWN, in CSS pixels, when that differs from its own pixel width (a
   * 1080-wide phone clip shown 180 wide). Lines and dots are sized for what is seen, so a canvas
   * shown small does not get hairlines. Omitted = shown at its own size. */
  displayWidth?: number;
};

const BONE_JOINTS: readonly string[] = Array.from(new Set(SKELETON_BONES.flat()));

/**
 * Draw the bones and joints for one frame. `points` are in CANVAS pixels, keyed by landmark name
 * (left_shoulder, …); a joint that is missing simply leaves its bones out.
 */
export function drawSkeleton(
  g: CanvasRenderingContext2D,
  points: ReadonlyMap<string, SkeletonPoint>,
  canvasWidth: number,
  style: SkeletonStyle = {},
): void {
  const shown = style.displayWidth && style.displayWidth > 0 ? style.displayWidth : canvasWidth;
  const perShownPixel = canvasWidth / shown; // canvas pixels per pixel on screen
  g.lineWidth = Math.max(2, shown / 300) * perShownPixel;
  g.lineCap = "round";
  g.strokeStyle = style.bone ?? "#00ea3c";
  for (const [a, b] of SKELETON_BONES) {
    const pa = points.get(a);
    const pb = points.get(b);
    if (!pa || !pb) continue;
    g.beginPath();
    g.moveTo(pa.x, pa.y);
    g.lineTo(pb.x, pb.y);
    g.stroke();
  }
  g.fillStyle = style.joint ?? "#00CCFF";
  const radius = Math.max(4, shown / 160) * perShownPixel;
  for (const name of style.joints ?? BONE_JOINTS) {
    const p = points.get(name);
    if (!p) continue;
    g.beginPath();
    g.arc(p.x, p.y, radius, 0, Math.PI * 2);
    g.fill();
  }
}
